import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { signInWithGoogle } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default function Auth() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "signin");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const endpoint = mode === "signin" ? "login" : "register";
            const payload = mode === "signin" ? { email, password } : { email, password, name };
            await axios.post(`${API}/auth/${endpoint}`, payload, { withCredentials: true });
            const pending = sessionStorage.getItem("pending_invite");
            if (pending) {
                sessionStorage.removeItem("pending_invite");
                navigate(`/join/${pending}`, { replace: true });
            } else {
                navigate("/onboarding", { replace: true });
            }
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
        setLoading(false);
    };

    return (
        <main data-testid="auth-page" className="flex min-h-screen items-center justify-center bg-cream px-6 py-16 text-navy">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                className="w-full max-w-md rounded-3xl border-2 border-warmgray bg-ivory p-8 shadow-xl shadow-navy/5 md:p-10"
            >
                <Link to="/" data-testid="auth-logo" aria-label="Ago GenApp home">
                    <img src="/logo.png" alt="Ago GenApp" className="mx-auto h-11 w-auto" />
                </Link>
                <p className="mt-5 text-center font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C98A94]">
                    Step 1 of 3 · Your account
                </p>
                <h1 className="mt-3 text-center font-display text-2xl md:text-3xl">
                    {mode === "signin" ? "Welcome back." : "Create your account."}
                </h1>

                <button
                    data-testid="auth-google-button"
                    onClick={signInWithGoogle}
                    className="btn-gradient mt-7 w-full rounded-full py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                    Continue with Google
                </button>

                <div className="my-6 flex items-center gap-4">
                    <span className="h-px flex-1 bg-warmgray" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-navy/60">or with email</span>
                    <span className="h-px flex-1 bg-warmgray" />
                </div>

                <div className="mb-5 grid grid-cols-2 gap-2 rounded-full border-2 border-warmgray p-1">
                    {[
                        { id: "signin", label: "Sign in" },
                        { id: "register", label: "Create account" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            data-testid={`auth-tab-${t.id}`}
                            onClick={() => { setMode(t.id); setError(""); }}
                            className={`rounded-full py-2.5 text-sm font-semibold transition-colors duration-300 ${
                                mode === t.id ? "bg-navy text-white" : "text-navy hover:bg-cream"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={submit} className="space-y-3">
                    {mode === "register" && (
                        <input
                            data-testid="auth-name-input"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Your name"
                            aria-label="Your name"
                            className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-3.5 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach"
                        />
                    )}
                    <input
                        data-testid="auth-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@yourfamily.com"
                        aria-label="Email address"
                        className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-3.5 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach"
                    />
                    <input
                        data-testid="auth-password-input"
                        type="password"
                        required
                        minLength={mode === "register" ? 8 : 1}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={mode === "register" ? "Password (min. 8 characters)" : "Password"}
                        aria-label="Password"
                        className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] px-6 py-3.5 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach"
                    />
                    {error && <p data-testid="auth-error" className="text-sm text-red-700">{error}</p>}
                    <button
                        data-testid="auth-submit-button"
                        type="submit"
                        disabled={loading}
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 text-sm font-semibold text-white shadow-lg shadow-navy/25 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {loading && <Loader2 size={15} className="animate-spin" />}
                        {mode === "signin" ? "Sign in" : "Create account"}
                    </button>
                </form>

                <Link to="/" data-testid="auth-back-home" className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-navy hover:text-navy/70">
                    <ArrowLeft size={15} /> Back Home
                </Link>
            </motion.div>
        </main>
    );
}
